import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import {
  getRequisites,
  putRequisites,
  issueInvoice,
  listInvoices,
  markInvoicePaid,
  fetchInvoicePdf,
  type Requisites,
  type InvoiceSummary,
} from '../api/billing';
import { getApiClient } from '../api/factory';
import { useAuthStore } from '../store/auth';
import { useT, type DictKey } from '../i18n';

const PLAN_KEY: Record<string, DictKey> = {
  pro: 'billing.plan.pro',
  business: 'billing.plan.business',
  enterprise: 'billing.plan.enterprise',
};

const STATUS_KEY: Record<string, DictKey> = {
  issued: 'billing.inv.statusIssued',
  paid: 'billing.inv.statusPaid',
  canceled: 'billing.inv.statusCanceled',
};

function formatRub(minor: number): string {
  return new Intl.NumberFormat('ru-RU').format(Math.round(minor / 100)) + ' ₽';
}

// Оплата по счёту (ANX-060b): реквизиты плательщика + выставление счетов + их
// список со скачиванием PDF и ручной отметкой об оплате. Рендерится внутри
// admin-only страницы «Тариф и оплата».
export function InvoiceSection() {
  const t = useT();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [plan, setPlan] = useState('pro');

  const reqQuery = useQuery({
    queryKey: ['billing_requisites'],
    queryFn: () => getRequisites(getApiClient()),
  });
  const invQuery = useQuery({
    queryKey: ['billing_invoices'],
    queryFn: () => listInvoices(getApiClient()),
  });

  const [draft, setDraft] = useState<Requisites>({
    kind: 'legal',
    name: '',
    inn: '',
    kpp: '',
    address: '',
    email: '',
  });
  const loaded = reqQuery.data;
  useEffect(() => {
    if (loaded) setDraft(loaded);
  }, [loaded]);

  const saveMu = useMutation({
    mutationFn: () => putRequisites(getApiClient(), draft),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['billing_requisites'] });
    },
    onError: () => setError(t('billing.inv.saveError')),
  });

  const issueMu = useMutation({
    mutationFn: () => issueInvoice(getApiClient(), plan),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['billing_invoices'] });
    },
    onError: () => setError(t('billing.inv.issueError')),
  });

  const markMu = useMutation({
    mutationFn: (id: string) => markInvoicePaid(getApiClient(), id),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['billing_invoices'] });
      qc.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: () => setError(t('billing.inv.issueError')),
  });

  async function openPdf(id: string, kind: 'invoice' | 'act') {
    try {
      const origin = (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? '';
      const token = useAuthStore.getState().session?.access ?? '';
      const blob = await fetchInvoicePdf(origin, token, id, kind);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Освобождаем URL чуть позже — даём вкладке успеть открыть.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError(t('billing.inv.pdfError'));
    }
  }

  const requisitesReady = !!loaded?.name && !!loaded?.inn;
  const invoices = invQuery.data ?? [];
  const field =
    'mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-sm focus:border-accent-500 focus:ring-accent-500';

  return (
    <section className="rounded-md border border-slate-200 bg-white p-5 shadow-card">
      <div className="mb-1 flex items-center gap-2">
        <FileText className="h-4 w-4 text-accent-600" aria-hidden="true" />
        <h2 className="text-sm font-medium text-slate-900">{t('billing.inv.heading')}</h2>
      </div>
      <p className="mb-4 text-xs text-slate-500">{t('billing.inv.hint')}</p>

      {error && (
        <p role="alert" className="mb-3 text-xs text-red-600">
          {error}
        </p>
      )}

      {/* Реквизиты плательщика */}
      <h3 className="mb-2 text-sm font-medium text-slate-800">
        {t('billing.inv.requisitesHeading')}
      </h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm text-slate-700">
          <span>{t('billing.inv.kind')}</span>
          <select
            value={draft.kind}
            onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
            className={field}
          >
            <option value="legal">{t('billing.inv.kindLegal')}</option>
            <option value="ip">{t('billing.inv.kindIp')}</option>
          </select>
        </label>
        <label className="block text-sm text-slate-700">
          <span>{t('billing.inv.name')}</span>
          <input
            type="text"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className={field}
          />
        </label>
        <label className="block text-sm text-slate-700">
          <span>{t('billing.inv.inn')}</span>
          <input
            type="text"
            value={draft.inn}
            onChange={(e) => setDraft({ ...draft, inn: e.target.value })}
            className={field}
          />
        </label>
        {draft.kind === 'legal' && (
          <label className="block text-sm text-slate-700">
            <span>{t('billing.inv.kpp')}</span>
            <input
              type="text"
              value={draft.kpp}
              onChange={(e) => setDraft({ ...draft, kpp: e.target.value })}
              className={field}
            />
          </label>
        )}
        <label className="block text-sm text-slate-700">
          <span>{t('billing.inv.address')}</span>
          <input
            type="text"
            value={draft.address}
            onChange={(e) => setDraft({ ...draft, address: e.target.value })}
            className={field}
          />
        </label>
        <label className="block text-sm text-slate-700">
          <span>{t('billing.inv.email')}</span>
          <input
            type="email"
            value={draft.email}
            onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            className={field}
          />
        </label>
      </div>
      <button
        type="button"
        onClick={() => saveMu.mutate()}
        disabled={saveMu.isPending}
        className="mt-3 rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
      >
        {t('common.save')}
      </button>

      {/* Выставление счёта */}
      <h3 className="mb-2 mt-6 text-sm font-medium text-slate-800">
        {t('billing.inv.issueHeading')}
      </h3>
      <div className="flex flex-wrap items-end gap-3">
        <label className="block text-sm text-slate-700">
          <span>{t('billing.inv.issuePlan')}</span>
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            className={field + ' w-48'}
          >
            <option value="pro">{t('billing.plan.pro')}</option>
            <option value="business">{t('billing.plan.business')}</option>
            <option value="enterprise">{t('billing.plan.enterprise')}</option>
          </select>
        </label>
        <button
          type="button"
          onClick={() => issueMu.mutate()}
          disabled={issueMu.isPending || !requisitesReady}
          className="rounded-md border border-accent-600 px-3 py-1.5 text-sm font-medium text-accent-700 hover:bg-accent-50 disabled:opacity-50"
        >
          {issueMu.isPending ? t('billing.inv.issuing') : t('billing.inv.issueButton')}
        </button>
      </div>
      {!requisitesReady && (
        <p className="mt-1 text-xs text-slate-500">{t('billing.inv.needRequisites')}</p>
      )}

      {/* Список счетов */}
      <h3 className="mb-2 mt-6 text-sm font-medium text-slate-800">
        {t('billing.inv.listHeading')}
      </h3>
      {invoices.length === 0 ? (
        <p className="text-sm text-slate-500">{t('billing.inv.empty')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="py-1.5 pr-3">{t('billing.inv.colNumber')}</th>
                <th className="py-1.5 pr-3">{t('billing.inv.colPlan')}</th>
                <th className="py-1.5 pr-3">{t('billing.inv.colPeriod')}</th>
                <th className="py-1.5 pr-3">{t('billing.inv.colAmount')}</th>
                <th className="py-1.5 pr-3">{t('billing.inv.colStatus')}</th>
                <th className="py-1.5">{t('billing.inv.colActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map((inv: InvoiceSummary) => (
                <tr key={inv.id}>
                  <td className="py-2 pr-3 font-medium text-slate-900">{inv.number}</td>
                  <td className="py-2 pr-3">{t(PLAN_KEY[inv.plan] ?? 'billing.plan.pro')}</td>
                  <td className="py-2 pr-3 text-slate-500">
                    {inv.periodStart} — {inv.periodEnd}
                  </td>
                  <td className="py-2 pr-3">{formatRub(inv.amountMinor)}</td>
                  <td className="py-2 pr-3">{t(STATUS_KEY[inv.status] ?? 'billing.inv.statusIssued')}</td>
                  <td className="py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openPdf(inv.id, 'invoice')}
                        className="text-accent-700 hover:underline"
                      >
                        {t('billing.inv.downloadInvoice')}
                      </button>
                      {inv.status === 'paid' && (
                        <button
                          type="button"
                          onClick={() => openPdf(inv.id, 'act')}
                          className="text-accent-700 hover:underline"
                        >
                          {t('billing.inv.downloadAct')}
                        </button>
                      )}
                      {inv.status === 'issued' && (
                        <button
                          type="button"
                          onClick={() => markMu.mutate(inv.id)}
                          disabled={markMu.isPending}
                          className="rounded border border-emerald-300 px-2 py-0.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                        >
                          {markMu.isPending ? t('billing.inv.marking') : t('billing.inv.markPaid')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
